import { darkenHex } from './colorMath';

// Functional chart colors — fixed, never from CHART_SERIES_COLORS.
// Token source: --punchup-color-chart-* in src/styles/punchup-tokens.css.
// Two-tier logic: dynamic (ticket types) = vivid/saturated; functional (states) =
// muted mid-tone, all at ~65–80% lightness so they form a coherent supporting group.
//
// Hue semantics:
//   available → border-grey  (#E7E7E7) — "empty"  — matches card chrome, nearly invisible
//   on-hold   → slate-400    (L=73%)   — "frozen" — cool/reserved
//   expired   → stone-400    (L=66%)   — "past"   — warm/done, consistent weight with hold/comp
//   comped    → violet-400   (L=72%)   — "gift"   — distinctively purple, not alarming

export const CHART_HOLD_COLOR = '#94A3B8'; // --punchup-color-chart-hold    Tailwind slate-400
export const CHART_EXPIRED_COLOR = '#A8A29E'; // --punchup-color-chart-expired Tailwind stone-400
export const CHART_COMP_COLOR = '#A78BFA'; // --punchup-color-chart-comp    Tailwind violet-400
// The Punchup primary used for the single-series area chart. Sourced here (not theme.colors.punchupOrange)
// so the green chart stays on the --punchup-color-primary value and is independent of the dark-theme
// styled-components palette (which legacy keeps at its own value). Mirror any token change in both repos.
export const CHART_PRIMARY_COLOR = '#FF5E2A'; // --punchup-color-primary

/**
 * Eight warm terracotta/brown shades for hold pool segments.
 * Entirely separate from CHART_SERIES_COLORS — no collision with ticket-type
 * colors regardless of how many types the event has.
 * All shades sit in the hue band 16–32° (orange-brown), lightness 28–75%,
 * so they read as a coherent "pool" family without competing with the vivid
 * ticket-type palette.
 */
export const CHART_POOL_COLORS: readonly string[] = [
	'#C4714A', // 1 — warm terracotta     hsl(23, 48%, 52%)
	'#8C5A34', // 2 — medium chestnut     hsl(22, 43%, 37%)
	'#DCA07C', // 3 — light caramel       hsl(25, 56%, 67%)
	'#6B3F23', // 4 — dark espresso       hsl(21, 51%, 28%)
	'#B87F40', // 5 — golden amber-brown  hsl(32, 48%, 48%)
	'#E3B89E', // 6 — sand / linen        hsl(22, 59%, 75%)
	'#7D5436', // 7 — walnut              hsl(22, 37%, 35%)
	'#A07865', // 8 — rosewood            hsl(16, 24%, 51%)
] as const;

// Pool-colour band parameters. The hue stays inside the documented [15–45°]
// orange-brown band (BASE 15 + at most SPAN 30); saturation/lightness alternate
// in small steps so successive indices read as the same "pool" family.
const POOL_HUE_BASE = 15;
const POOL_HUE_SPAN = 30;
const POOL_HUE_STEP = 11;
const POOL_SAT_BASE = 42;
const POOL_SAT_SWING = 8;
const POOL_LIGHT_BASE = 35;
const POOL_LIGHT_STEP = 15;

/** Generates a pool color for index ≥ 8 by rotating within the hue band [15–45°]. */
export function generatePoolColor(index: number): string {
	const hue = POOL_HUE_BASE + ((index * POOL_HUE_STEP) % POOL_HUE_SPAN);
	const saturation = POOL_SAT_BASE + (index % 2) * POOL_SAT_SWING;
	const lightness = POOL_LIGHT_BASE + (index % 3) * POOL_LIGHT_STEP;
	return hslToHex(hue, saturation, lightness);
}

// ── Shared colour math ───────────────────────────────────────────────────────────────────────────
// The hex helpers live in colorMath.ts (the single source shared with the tag/picker colour logic);
// re-exported here so chart-side importers (inventoryUtils, the Show-Day donut) keep one import path.
export { hexToRgb, rgbToHex, lightenHex, darkenHex } from './colorMath';

/**
 * Darker proportional outline for a chart segment's FILL on the white card surface — gives bars and
 * donut slices a crisp, more-contrasted edge and separates adjacent (touching) segments. Derived from
 * the fill (not a fixed colour), mirroring how the tag derives a readable shade from its colour
 * (`chipColor`) — both blend via `darkenHex`. Blends 40% toward black: visible against light fills,
 * still a defined edge on the deep jewel-tone palette.
 */
export function chartContrastStroke(hex: string): string {
	return darkenHex(hex, 0.4);
}

function hslToHex(h: number, s: number, l: number): string {
	const sl = s / 100;
	const ll = l / 100;
	const a = sl * Math.min(ll, 1 - ll);
	const f = (n: number) => {
		const k = (n + h / 30) % 12;
		const c = ll - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
		return Math.round(255 * c)
			.toString(16)
			.padStart(2, '0');
	};
	return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Ordered fallback palette for ticket-type SERIES colours — the colour a tier shows on its chart
 * series / table tag / tile rail when it has no stored colour of its own. Eight deep "jewel-tone" hues
 * from the PunchupColorPicker preset family (`PRESET_COLORS`), so an auto-assigned colour looks like one
 * the manager could have picked.
 *
 * Two reserved-colour exclusions (same principle, two reasons):
 *   - **Orange** is the brand interaction colour (hover / focus / CTA) — never a category.
 *   - **The bright status hexes** (info-blue `#3b82f6`, danger-red `#ef4444`, comped-teal `#14b8a6`,
 *     addon-cyan `#06b6d4`, promo-violet `#8b5cf6`, success-lime, warning-yellow, inactive-grey) are
 *     excluded too: a ticket-type tag sits in the SAME table row as a status chip, so reusing a status
 *     hue would make a tier read like "Pending"/"Refunded"/"Comped". See src/styles/CLAUDE.md →
 *     "Status colour taxonomy". These are the deeper variants of those hues, which also keeps them
 *     high-contrast on the white iframe surface.
 *
 * Single source: consumed via `buildTicketTypeColorMap` (features/common/utils/ticketTypeColors) by the
 * analytics palette, the ticket-type table tags, the pool member chips, and the ticket-type tile rails —
 * one palette so a tier reads the SAME fallback colour on every page.
 *
 * Every entry is a member of `PunchupColorPicker`'s `PRESET_COLORS`, so an auto colour is always one the
 * manager could also have picked. **Ordered diversity-first**: the first slots are a distinct hue per
 * family spread around the wheel (blue → pink → green → red → violet → teal → yellow-green), so a venue
 * with a handful of tiers gets maximally-separable colours; later slots narrow to nearby hues (a second
 * blue/pink/green) only once the obvious ones are spent.
 *
 * Usage (charts):
 *   import { CHART_SERIES_COLORS } from 'features/common/utils/chartColors';
 *   bars={dataKeys.map((key, i) => ({ dataKey: key, color: CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length], name: key }))}
 */
export const CHART_SERIES_COLORS: readonly string[] = [
	// ── Distinct hue per family — spread around the wheel for early separability ──
	'#1E40AF', // 1 — indigo        (blue)
	'#EC4899', // 2 — pink          (magenta)
	'#166534', // 3 — forest green  (green)
	'#B91C1C', // 4 — brick         (red)
	'#5B21B6', // 5 — violet        (purple)
	'#0F766E', // 6 — deep teal     (teal)
	'#4D7C0F', // 7 — olive         (yellow-green)
	// ── Narrowing: second of a family, nearby the ones above ──
	'#155E75', // 8 — petrol        (between teal & blue)
	'#9D174D', // 9 — dark magenta  (near pink)
	'#22C55E', // 10 — emerald      (near forest)
] as const;
